import { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { InterviewQuestion, InterviewQuestions, InterviewQuestionsChildren } from '@/types';
import {
  isGroup,
  isQuestion,
  findItemPath,
  getGroupAtPath,
  findSubgroupById,
  removeQuestionFromTree,
  removeSubgroupFromTree,
  createEmptyGroup,
} from '@/utils/questionGroupHelpers';

interface UseDragAndDropHandlersProps {
  groupStructure: InterviewQuestions;
  showGroupUI: boolean;
  setGroupStructure: (structure: InterviewQuestions) => void;
  setIsFormChanged: (changed: boolean) => void;
  setReorderChanged: (changed: boolean) => void;
}

export const useDragAndDropHandlers = ({
  groupStructure,
  showGroupUI,
  setGroupStructure,
  setIsFormChanged,
  setReorderChanged,
}: UseDragAndDropHandlersProps) => {
  // ==================== REORDER HANDLERS ====================

  const handleReorderQuestionsInGroup = (
    groupIndex: number,
    reorderedQuestions: InterviewQuestion[],
    groupPath?: number[],
  ) => {
    const path = groupPath || [groupIndex];

    // Update the group structure
    const updateStructureWithReorderedQuestions = (
      structure: InterviewQuestions,
      targetPath: number[],
      newQuestions: InterviewQuestion[],
      depth: number = 0,
    ): InterviewQuestions => {
      if (depth === targetPath.length) {
        return {
          ...structure,
          children: newQuestions,
        };
      }

      const idx = targetPath[depth];
      return {
        ...structure,
        children: structure.children.map((child, i) => {
          if (i === idx && isGroup(child)) {
            return updateStructureWithReorderedQuestions(
              child,
              targetPath,
              newQuestions,
              depth + 1,
            );
          }
          return child;
        }) as InterviewQuestionsChildren,
      };
    };

    const updatedStructure = updateStructureWithReorderedQuestions(
      groupStructure,
      path,
      reorderedQuestions,
    );
    setGroupStructure(updatedStructure);
    setReorderChanged(true);
  };

  const handleReorderSubgroupsInGroup = (
    groupIndex: number,
    reorderedSubgroups: InterviewQuestions[],
    groupPath?: number[],
  ) => {
    const path = groupPath || [groupIndex];

    const updateStructureWithReorderedSubgroups = (
      structure: InterviewQuestions,
      targetPath: number[],
      newSubgroups: InterviewQuestions[],
      depth: number = 0,
    ): InterviewQuestions => {
      if (depth === targetPath.length) {
        return {
          ...structure,
          children: newSubgroups,
        };
      }

      const idx = targetPath[depth];
      return {
        ...structure,
        children: structure.children.map((child, i) => {
          if (i === idx && isGroup(child)) {
            return updateStructureWithReorderedSubgroups(
              child,
              targetPath,
              newSubgroups,
              depth + 1,
            );
          }
          return child;
        }) as InterviewQuestionsChildren,
      };
    };

    const updatedStructure = updateStructureWithReorderedSubgroups(
      groupStructure,
      path,
      reorderedSubgroups,
    );
    setGroupStructure(updatedStructure);
    setIsFormChanged(true);
  };

  // ==================== MOVE HANDLERS ====================

  const handleMoveQuestionBetweenGroups = (
    questionId: string,
    fromPath: number[],
    toPath: number[],
    isDroppedOntoDroppableZone: boolean = false,
  ) => {
    // Find the question in the tree structure
    const findQuestionById = (
      structure: InterviewQuestions,
      targetId: string,
    ): InterviewQuestion | null => {
      return structure.children.reduce<InterviewQuestion | null>((found, child) => {
        if (found) return found;

        if (isQuestion(child) && child.id === targetId) {
          return child;
        }

        if (isGroup(child)) {
          return findQuestionById(child, targetId);
        }

        return null;
      }, null);
    };

    const originalQuestion = findQuestionById(groupStructure, questionId);
    if (!originalQuestion) return;

    const question = structuredClone(originalQuestion) as InterviewQuestion;

    const fromParentPath = fromPath.slice(0, -1);

    // Determine the parent path for the drop target
    let toParentPath: number[];
    if (isDroppedOntoDroppableZone && toPath.length > 1) {
      toParentPath = toPath;
    } else if (toPath.length === 1) {
      toParentPath = toPath;
    } else {
      toParentPath = toPath.slice(0, -1);
    }

    const isSameGroup =
      fromParentPath.length === toParentPath.length &&
      fromParentPath.every((val, idx) => val === toParentPath[idx]);

    if (isSameGroup) {
      const parentGroup = getGroupAtPath(groupStructure, fromParentPath);
      if (!parentGroup) return;

      const groupQuestions = parentGroup.children.filter(isQuestion);
      const fromIndex = fromPath[fromPath.length - 1];
      const toIndex = toPath[toPath.length - 1];

      const reorderedQuestions = arrayMove(groupQuestions, fromIndex, toIndex);
      handleReorderQuestionsInGroup(
        fromParentPath[fromParentPath.length - 1],
        reorderedQuestions,
        fromParentPath,
      );
      return;
    }

    // Helper to insert question with type conversion
    const insertQuestionWithConversion = (
      structure: InterviewQuestions,
      targetPath: number[],
      questionToInsert: InterviewQuestion,
      depth: number = 0,
    ): InterviewQuestions => {
      if (depth === targetPath.length) {
        const hasQuestions = structure.children.some(isQuestion);
        const hasSubgroups = structure.children.some(isGroup);
        const isTopLevel = targetPath.length === 1;

        if (hasQuestions && !hasSubgroups) {
          return {
            ...structure,
            children: [...structure.children, questionToInsert] as InterviewQuestion[],
          };
        }

        if (hasSubgroups && !hasQuestions) {
          if (isTopLevel) {
            const existingSubgroups = structure.children.filter(isGroup);
            const newSubgroupNumber = existingSubgroups.length + 1;
            const newSubgroup = createEmptyGroup(`Subgroup ${newSubgroupNumber}`);
            newSubgroup.children = [questionToInsert];

            return {
              ...structure,
              children: [...structure.children, newSubgroup] as InterviewQuestions[],
            };
          }
          return {
            ...structure,
            children: [...structure.children, questionToInsert] as InterviewQuestion[],
          };
        }

        if (structure.children.length === 0) {
          return {
            ...structure,
            children: [questionToInsert],
          };
        }

        return {
          ...structure,
          children: [...structure.children, questionToInsert] as InterviewQuestion[],
        };
      }

      const idx = targetPath[depth];
      return {
        ...structure,
        children: structure.children.map((child, i) => {
          if (i === idx && isGroup(child)) {
            return insertQuestionWithConversion(child, targetPath, questionToInsert, depth + 1);
          }
          return child;
        }) as InterviewQuestionsChildren,
      };
    };

    let updatedStructure = removeQuestionFromTree(groupStructure, questionId);
    updatedStructure = insertQuestionWithConversion(updatedStructure, toParentPath, question);

    setGroupStructure(updatedStructure);
    setIsFormChanged(true);
  };

  const handleMoveSubgroupBetweenGroups = (
    subgroupId: string,
    fromPath: number[],
    toPath: number[],
  ) => {
    const subgroupToDrag = findSubgroupById(groupStructure, subgroupId);
    if (!subgroupToDrag) return;

    const subgroup = structuredClone(subgroupToDrag) as InterviewQuestions;

    const fromParentPath = fromPath.slice(0, -1);
    const toParentPath = toPath.length === 1 ? toPath : toPath.slice(0, -1);

    const isSameParent =
      fromParentPath.length === toParentPath.length &&
      fromParentPath.every((val, idx) => val === toParentPath[idx]);

    if (isSameParent) {
      const parentGroup = getGroupAtPath(groupStructure, fromParentPath);
      if (!parentGroup) return;

      const subgroups = parentGroup.children.filter(isGroup);
      const fromIndex = fromPath[fromPath.length - 1];
      const toIndex = toPath[toPath.length - 1];

      const reorderedSubgroups = arrayMove(subgroups, fromIndex, toIndex);
      handleReorderSubgroupsInGroup(
        fromParentPath[fromParentPath.length - 1],
        reorderedSubgroups,
        fromParentPath,
      );
      return;
    }

    // Helper to insert subgroup with type conversion
    const insertSubgroupIntoGroup = (
      structure: InterviewQuestions,
      targetPath: number[],
      subgroupToInsert: InterviewQuestions,
      depth: number = 0,
    ): InterviewQuestions => {
      if (depth === targetPath.length) {
        const hasQuestions = structure.children.some(isQuestion);
        const hasSubgroups = structure.children.some(isGroup);

        if (structure.children.length === 0) {
          return {
            ...structure,
            children: [subgroupToInsert],
          };
        }

        if (hasSubgroups && !hasQuestions) {
          return {
            ...structure,
            children: [...structure.children, subgroupToInsert] as InterviewQuestions[],
          };
        }

        if (hasQuestions && !hasSubgroups) {
          const existingQuestions = structure.children.filter(isQuestion);

          const subgroup1 = createEmptyGroup('Subgroup 1');
          subgroup1.children = existingQuestions;

          const subgroup2 = { ...subgroupToInsert, name: 'Subgroup 2' };

          return {
            ...structure,
            children: [subgroup1, subgroup2],
          };
        }

        return {
          ...structure,
          children: [...structure.children, subgroupToInsert] as InterviewQuestions[],
        };
      }

      const idx = targetPath[depth];
      return {
        ...structure,
        children: structure.children.map((child, i) => {
          if (i === idx && isGroup(child)) {
            return insertSubgroupIntoGroup(child, targetPath, subgroupToInsert, depth + 1);
          }
          return child;
        }) as InterviewQuestionsChildren,
      };
    };

    let updatedStructure = removeSubgroupFromTree(groupStructure, subgroupId);
    updatedStructure = insertSubgroupIntoGroup(updatedStructure, toParentPath, subgroup);

    setGroupStructure(updatedStructure);
    setIsFormChanged(true);
  };

  // ==================== DRAG EVENT HANDLERS ====================

  const handleDragStart = (
    event: DragStartEvent,
    setIsActiveDrag: (active: boolean) => void,
    setActiveId: (id: string | null) => void,
  ) => {
    setIsActiveDrag(true);
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (
    event: DragEndEvent,
    setIsActiveDrag: (active: boolean) => void,
    setActiveId: (id: string | null) => void,
  ) => {
    const { active, over } = event;

    setIsActiveDrag(false);
    setActiveId(null);

    if (!over || active?.id === over?.id) {
      return;
    }

    if (showGroupUI) {
      const allGroups = groupStructure.children.filter(isGroup);

      const isDraggingTopLevelGroup = allGroups.some((g) => (g.id || g.name) === active.id);

      if (isDraggingTopLevelGroup) {
        const activeIndex = allGroups.findIndex((g) => (g.id || g.name) === active.id);
        const overIndex = allGroups.findIndex((g) => (g.id || g.name) === over.id);

        if (activeIndex !== -1 && overIndex !== -1) {
          const reorderedGroups = arrayMove(allGroups, activeIndex, overIndex);
          setGroupStructure({
            ...groupStructure,
            children: reorderedGroups,
          });
          setIsFormChanged(true);
        }
      } else {
        let activePath = findItemPath(active.id as string, true, groupStructure);
        const isDraggingQuestion = activePath !== null;

        if (!activePath) {
          activePath = findItemPath(active.id as string, false, groupStructure);
        }

        if (!activePath) {
          return;
        }

        let overPath: number[] | null = null;
        let isDroppedOntoDroppableZone = false;

        const findGroupPathById = (targetId: string): number[] | null => {
          const topLevelIndex = allGroups.findIndex((g) => (g.id || g.name) === targetId);
          if (topLevelIndex !== -1) {
            return [topLevelIndex];
          }

          // Use traditional for loop instead of for-of to comply with linting rules
          for (let topIndex = 0; topIndex < allGroups.length; topIndex += 1) {
            const topGroup = allGroups[topIndex];

            const searchInGroup = (
              groupToSearch: InterviewQuestions,
              currentPath: number[],
            ): number[] | null => {
              const subgroups = groupToSearch.children.filter(isGroup);

              for (let i = 0; i < subgroups.length; i += 1) {
                const subgroup = subgroups[i];
                const subgroupPath = [...currentPath, i];

                if ((subgroup.id || subgroup.name) === targetId) {
                  return subgroupPath;
                }

                const deeperPath = searchInGroup(subgroup, subgroupPath);
                if (deeperPath) return deeperPath;
              }

              return null;
            };

            const foundPath = searchInGroup(topGroup, [topIndex]);
            if (foundPath) return foundPath;
          }

          return null;
        };

        const overIdStr = over.id as string;
        if (overIdStr.startsWith('group-droppable-')) {
          isDroppedOntoDroppableZone = true;
          const groupId = overIdStr.replace('group-droppable-', '');
          overPath = findGroupPathById(groupId);
        } else {
          const groupPath = findGroupPathById(overIdStr);
          if (groupPath) {
            isDroppedOntoDroppableZone = true;
            overPath = groupPath;
          }
        }

        if (!overPath) {
          overPath = findItemPath(over.id as string, true, groupStructure);

          if (!overPath) {
            overPath = findItemPath(over.id as string, false, groupStructure);
          }
        }

        if (!overPath) {
          return;
        }

        if (isDraggingQuestion) {
          handleMoveQuestionBetweenGroups(
            active.id as string,
            activePath,
            overPath,
            isDroppedOntoDroppableZone,
          );
        } else {
          handleMoveSubgroupBetweenGroups(active.id as string, activePath, overPath);
        }
      }
    } else {
      // Flat mode: reordering questions at root level
      const flatQuestions = groupStructure.children.filter(isQuestion);
      const activeIndex = flatQuestions.findIndex((q) => q?.id === active?.id);
      const overIndex = flatQuestions.findIndex((q) => q?.id === over?.id);

      if (activeIndex !== -1 && overIndex !== -1) {
        const reorderedQuestions = arrayMove(flatQuestions, activeIndex, overIndex);

        setGroupStructure({
          ...groupStructure,
          children: reorderedQuestions,
        });
        setReorderChanged(true);
      }
    }
  };

  return {
    handleDragStart,
    handleDragEnd,
    handleReorderQuestionsInGroup,
    handleReorderSubgroupsInGroup,
    handleMoveQuestionBetweenGroups,
    handleMoveSubgroupBetweenGroups,
  };
};
